import { openDB } from 'idb';

export const DB_NAME = 'spareparts-db';
export const DB_VERSION = 15;

export async function initDatabase() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // Parts Store
      if (!db.objectStoreNames.contains('parts')) {
        const partsStore = db.createObjectStore('parts', { keyPath: 'id' });
        partsStore.createIndex('articleNumber', 'articleNumber', { unique: true });
        partsStore.createIndex('categoryId', 'categoryId');
        partsStore.createIndex('deviceCategory', 'deviceCategory');
        partsStore.createIndex('compatibleTypes', 'compatibleTypes', { multiEntry: true });
      }

      // Categories Store
      if (!db.objectStoreNames.contains('categories')) {
        const categoriesStore = db.createObjectStore('categories', { keyPath: 'id' });
        categoriesStore.createIndex('name', 'name', { unique: true });
      }

      // Devices Store
      if (!db.objectStoreNames.contains('devices')) {
        const devicesStore = db.createObjectStore('devices', { keyPath: 'id' });
        devicesStore.createIndex('type', 'type');
        devicesStore.createIndex('name', 'name');
      }

      // Cart Store
      if (!db.objectStoreNames.contains('cart')) {
        db.createObjectStore('cart', { keyPath: 'id' });
      }

      // Dispatcher Cart Store
      if (!db.objectStoreNames.contains('dispatcher_cart')) {
        db.createObjectStore('dispatcher_cart', { keyPath: 'uniqueId' });
      }

      // Orders Store
      if (!db.objectStoreNames.contains('orders')) {
        const ordersStore = db.createObjectStore('orders', { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        ordersStore.createIndex('status', 'status');
        ordersStore.createIndex('createdAt', 'createdAt');
      }

      // Part Images Store for offline caching
      if (!db.objectStoreNames.contains('part_images')) {
        const imagesStore = db.createObjectStore('part_images', { keyPath: 'partId' });
        imagesStore.createIndex('timestamp', 'timestamp');
      }

      // Device Images Store for offline caching
      if (!db.objectStoreNames.contains('device_images')) {
        const deviceImagesStore = db.createObjectStore('device_images', { keyPath: 'deviceId' });
        deviceImagesStore.createIndex('timestamp', 'timestamp');
      }

      // Sync Metadata
      if (!db.objectStoreNames.contains('sync_metadata')) {
        db.createObjectStore('sync_metadata', { keyPath: 'key' });
      }

      // Auth
      if (!db.objectStoreNames.contains('auth')) {
        db.createObjectStore('auth', { keyPath: 'key' });
      }

      // Favorites Store for offline support
      if (!db.objectStoreNames.contains('favorites')) {
        const favoritesStore = db.createObjectStore('favorites', { keyPath: 'partId' });
        favoritesStore.createIndex('createdAt', 'createdAt');
      }

      // Customers Store for offline support
      if (!db.objectStoreNames.contains('customers')) {
        const customersStore = db.createObjectStore('customers', { keyPath: 'id' });
        customersStore.createIndex('name', 'name');
        customersStore.createIndex('type', 'type');
      }

      // Documentation offline stores (version 10)
      if (!db.objectStoreNames.contains('documentation')) {
        const docStore = db.createObjectStore('documentation', { keyPath: 'id' });
        docStore.createIndex('device_id', 'device_id');
        docStore.createIndex('customer_id', 'customer_id');
      }

      if (!db.objectStoreNames.contains('doc_attachments')) {
        const attStore = db.createObjectStore('doc_attachments', { keyPath: 'id' });
        attStore.createIndex('documentation_id', 'documentation_id');
      }

      if (!db.objectStoreNames.contains('doc_attachment_files')) {
        // Stores actual file blobs for offline viewing
        db.createObjectStore('doc_attachment_files', { keyPath: 'attachId' });
      }

      if (!db.objectStoreNames.contains('subclients')) {
        const subStore = db.createObjectStore('subclients', { keyPath: 'id' });
        subStore.createIndex('customer_id', 'customer_id');
      }

      // News feeds offline stores (version 11)
      if (!db.objectStoreNames.contains('news_feeds')) {
        const newsFeedsStore = db.createObjectStore('news_feeds', { keyPath: 'id' });
        newsFeedsStore.createIndex('customer_id', 'customer_id');
        newsFeedsStore.createIndex('status', 'status');
      }

      if (!db.objectStoreNames.contains('news_feed_attachments')) {
        const nfAttStore = db.createObjectStore('news_feed_attachments', { keyPath: 'id' });
        nfAttStore.createIndex('feed_id', 'feed_id');
      }

      // News feed attachment file blobs for offline viewing (version 13)
      if (!db.objectStoreNames.contains('news_feed_attachment_files')) {
        db.createObjectStore('news_feed_attachment_files', { keyPath: 'attachId' });
      }

      // Easyhelp offline stores (version 11)
      if (!db.objectStoreNames.contains('easyhelp_cases')) {
        const ehCasesStore = db.createObjectStore('easyhelp_cases', { keyPath: 'id' });
        ehCasesStore.createIndex('customer_id', 'customer_id');
        ehCasesStore.createIndex('status', 'status');
        ehCasesStore.createIndex('created_by', 'created_by');
      }

      if (!db.objectStoreNames.contains('easyhelp_messages')) {
        const ehMsgStore = db.createObjectStore('easyhelp_messages', { keyPath: 'id' });
        ehMsgStore.createIndex('case_id', 'case_id');
      }

      if (!db.objectStoreNames.contains('easyhelp_file_cache')) {
        // Stores file blobs from easyhelp chat (images, attachments)
        db.createObjectStore('easyhelp_file_cache', { keyPath: 'filename' });
      }

      // Passwords offline store (version 14)
      if (!db.objectStoreNames.contains('passwords')) {
        const passwordsStore = db.createObjectStore('passwords', { keyPath: 'id' });
        passwordsStore.createIndex('customer_id', 'customer_id');
      }
    }
  });
}

// ====================================================
// Passwords – Offline Support
// ====================================================

export async function savePasswords(passwords) {
  const db = await initDatabase();
  const tx = db.transaction('passwords', 'readwrite');
  await tx.objectStore('passwords').clear();
  for (const pw of passwords) await tx.objectStore('passwords').put(pw);
  await tx.done;
}

export async function getPasswordsOffline() {
  const db = await initDatabase();
  return db.getAll('passwords');
}

// ====================================================
// Documentation – Offline Support
// ====================================================

export async function saveDocumentationForOffline(docs, attachments) {
  const db = await initDatabase();
  const tx = db.transaction(['documentation', 'doc_attachments'], 'readwrite');
  await tx.objectStore('documentation').clear();
  for (const doc of docs) await tx.objectStore('documentation').put(doc);
  await tx.objectStore('doc_attachments').clear();
  for (const att of attachments) await tx.objectStore('doc_attachments').put(att);
  await tx.done;
}

export async function getDocumentationCustomersOffline() {
  const db = await initDatabase();
  const docs = await db.getAll('documentation');
  // Synced customers store as fallback for resolving names (docs may have a
  // null/undefined customer_name, e.g. orphaned docs or non-customer-bound docs).
  const allCustomers = db.objectStoreNames.contains('customers')
    ? await db.getAll('customers')
    : [];
  const customerNameById = new Map(allCustomers.map(c => [c.id, c.name]));
  const customerMap = new Map();
  for (const doc of docs) {
    // Skip docs that aren't bound to a customer (e.g. category-level docs)
    if (doc.customer_id == null) continue;
    if (!customerMap.has(doc.customer_id)) {
      const name = doc.customer_name || customerNameById.get(doc.customer_id) || 'Unbekannter Kunde';
      customerMap.set(doc.customer_id, { id: doc.customer_id, name, doc_count: 0 });
    }
    customerMap.get(doc.customer_id).doc_count++;
  }
  return Array.from(customerMap.values()).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );
}

export async function getDocumentationDevicesOffline(customerId) {
  const db = await initDatabase();
  const docs = await db.getAll('documentation');
  const allDevices = db.objectStoreNames.contains('devices')
    ? await db.getAll('devices')
    : [];
  const deviceCategoryMap = new Map(allDevices.map(d => [d.id, d.category_id]));
  // Only device-level docs (exclude customer-level docs where device_id is null)
  const filtered = docs.filter(d => d.customer_id === customerId && d.device_id != null);
  const deviceMap = new Map();
  for (const doc of filtered) {
    if (!deviceMap.has(doc.device_id)) {
      deviceMap.set(doc.device_id, {
        id: doc.device_id,
        name: doc.device_name,
        type: doc.device_type,
        category_id: deviceCategoryMap.get(doc.device_id) ?? doc.device_category_id ?? null,
        doc_count: 0
      });
    }
    deviceMap.get(doc.device_id).doc_count++;
  }
  return Array.from(deviceMap.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

export async function getDeviceDocsOffline(deviceId) {
  const db = await initDatabase();
  const docs = await db.getAll('documentation');
  return docs.filter(d => d.device_id === deviceId);
}

export async function getCustomerDocsOffline(customerId) {
  const db = await initDatabase();
  const docs = await db.getAll('documentation');
  return docs.filter(d => d.customer_id === customerId && (d.device_id === null || d.device_id === undefined));
}

export async function getDocAttachmentsOffline(docId) {
  const db = await initDatabase();
  const all = await db.getAll('doc_attachments');
  return all.filter(a => a.documentation_id === docId);
}

export async function getDocAttachmentFileOffline(attachId) {
  const db = await initDatabase();
  return db.get('doc_attachment_files', attachId);
}

export async function saveDocAttachmentFile(attachId, blob) {
  const db = await initDatabase();
  return db.put('doc_attachment_files', { attachId, blob, timestamp: Date.now() });
}

// CRUD Operations für Parts
export async function getAllParts() {
  const db = await initDatabase();
  return db.getAll('parts');
}

export async function getPartById(id) {
  const db = await initDatabase();
  return db.get('parts', id);
}

export async function addPart(part) {
  const db = await initDatabase();
  return db.add('parts', part);
}

export async function updatePart(part) {
  const db = await initDatabase();
  return db.put('parts', part);
}

// Warenkorb-Funktionen
export async function getCart() {
  const db = await initDatabase();
  return db.getAll('cart');
}

export async function addToCart(partId, quantity = 1) {
  const db = await initDatabase();
  return db.put('cart', {
    partId,
    quantity,
    addedAt: new Date().toISOString()
  });
}

export async function removeFromCart(partId) {
  const db = await initDatabase();
  return db.delete('cart', partId);
}

export async function clearCart() {
  const db = await initDatabase();
  const tx = db.transaction('cart', 'readwrite');
  await tx.objectStore('cart').clear();
  await tx.done;
}

// Categories-Funktionen
export async function getAllCategories() {
  const db = await initDatabase();
  return db.getAll('categories');
}

// Devices-Funktionen
export async function getAllDevices() {
  const db = await initDatabase();
  return db.getAll('devices');
}

export async function getDeviceById(id) {
  const db = await initDatabase();
  return db.get('devices', id);
}

export async function getDevicesByType(type) {
  const db = await initDatabase();
  const index = db.transaction('devices').store.index('type');
  return index.getAll(type);
}

export async function addDevice(device) {
  const db = await initDatabase();
  return db.add('devices', device);
}

export async function updateDevice(device) {
  const db = await initDatabase();
  return db.put('devices', device);
}

// Favoriten-Funktionen
export async function getAllFavorites() {
  const db = await initDatabase();
  const favorites = await db.getAll('favorites');
  return favorites.map(f => f.partId);
}

export async function addFavoriteToDb(partId) {
  const db = await initDatabase();
  return db.put('favorites', {
    partId,
    createdAt: new Date().toISOString()
  });
}

export async function removeFavoriteFromDb(partId) {
  const db = await initDatabase();
  return db.delete('favorites', partId);
}

export async function saveFavorites(favoriteIds) {
  const db = await initDatabase();
  const tx = db.transaction('favorites', 'readwrite');
  
  // Lösche alle bestehenden
  await tx.objectStore('favorites').clear();
  
  // Füge neue hinzu
  for (const partId of favoriteIds) {
    await tx.objectStore('favorites').put({
      partId,
      createdAt: new Date().toISOString()
    });
  }
  
  await tx.done;
}

export async function isFavorite(partId) {
  const db = await initDatabase();
  const favorite = await db.get('favorites', partId);
  return !!favorite;
}

// ====================================================
// Customers - Offline Support
// ====================================================

export async function getAllCustomers() {
  const db = await initDatabase();
  return db.getAll('customers');
}

export async function getCustomerById(id) {
  const db = await initDatabase();
  return db.get('customers', id);
}

export async function saveCustomers(customers) {
  const db = await initDatabase();
  const tx = db.transaction('customers', 'readwrite');
  const store = tx.objectStore('customers');
  
  for (const customer of customers) {
    await store.put(customer);
  }
  
  await tx.done;
}

export async function clearCustomers() {
  const db = await initDatabase();
  const tx = db.transaction('customers', 'readwrite');
  await tx.objectStore('customers').clear();
  await tx.done;
}

// ====================================================
// News Feeds - Offline Support
// ====================================================

export async function saveNewsFeeds(feeds) {
  const db = await initDatabase();
  const tx = db.transaction('news_feeds', 'readwrite');
  await tx.objectStore('news_feeds').clear();
  for (const feed of feeds) await tx.objectStore('news_feeds').put(feed);
  await tx.done;
}

export async function saveNewsFeedAttachments(attachments) {
  const db = await initDatabase();
  const tx = db.transaction('news_feed_attachments', 'readwrite');
  await tx.objectStore('news_feed_attachments').clear();
  for (const att of attachments) await tx.objectStore('news_feed_attachments').put(att);
  await tx.done;
}

export async function getAllNewsFeeds() {
  const db = await initDatabase();
  return db.getAll('news_feeds');
}

export async function getNewsFeedAttachmentsByFeed(feedId) {
  const db = await initDatabase();
  const index = db.transaction('news_feed_attachments').store.index('feed_id');
  return index.getAll(feedId);
}

export async function saveNewsFeedAttachmentFile(attachId, blob) {
  const db = await initDatabase();
  return db.put('news_feed_attachment_files', { attachId, blob, timestamp: Date.now() });
}

export async function getNewsFeedAttachmentFileOffline(attachId) {
  const db = await initDatabase();
  return db.get('news_feed_attachment_files', attachId);
}

// ====================================================
// Easyhelp - Offline Support
// ====================================================

export async function saveEasyhelpCases(cases) {
  const db = await initDatabase();
  const tx = db.transaction('easyhelp_cases', 'readwrite');
  await tx.objectStore('easyhelp_cases').clear();
  for (const c of cases) await tx.objectStore('easyhelp_cases').put(c);
  await tx.done;
}

export async function saveEasyhelpMessages(messages) {
  const db = await initDatabase();
  const tx = db.transaction('easyhelp_messages', 'readwrite');
  await tx.objectStore('easyhelp_messages').clear();
  for (const m of messages) await tx.objectStore('easyhelp_messages').put(m);
  await tx.done;
}

export async function getAllEasyhelpCases() {
  const db = await initDatabase();
  return db.getAll('easyhelp_cases');
}

export async function getEasyhelpMessagesByCase(caseId) {
  const db = await initDatabase();
  const index = db.transaction('easyhelp_messages').store.index('case_id');
  return index.getAll(caseId);
}

export async function saveEasyhelpFileCache(filename, blob) {
  const db = await initDatabase();
  return db.put('easyhelp_file_cache', { filename, blob, timestamp: Date.now() });
}

export async function getEasyhelpFileCache(filename) {
  const db = await initDatabase();
  return db.get('easyhelp_file_cache', filename);
}

export async function saveEasyhelpMessagesForCase(caseId, messages) {
  const db = await initDatabase();
  const tx = db.transaction('easyhelp_messages', 'readwrite');
  const store = tx.objectStore('easyhelp_messages');
  
  // Lösche zuerst alle alten Nachrichten für diesen konkreten Fall
  const index = store.index('case_id');
  let cursor = await index.openCursor(IDBKeyRange.only(caseId));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  
  // Füge die neuen Nachrichten hinzu
  for (const m of messages) {
    await store.put(m);
  }
  
  await tx.done;
}

export async function saveEasyhelpMessageOffline(message) {
  const db = await initDatabase();
  return db.put('easyhelp_messages', message);
}

export async function getDocumentationByIdOffline(id) {
  const db = await initDatabase();
  return db.get('documentation', id);
}

export async function getAllDocumentationOffline() {
  const db = await initDatabase();
  return db.getAll('documentation');
}

export async function saveSubclients(subclients) {
  const db = await initDatabase();
  const tx = db.transaction('subclients', 'readwrite');
  const store = tx.objectStore('subclients');
  for (const s of subclients) {
    await store.put(s);
  }
  await tx.done;
}

export async function getSubclientsOffline(customerId) {
  const db = await initDatabase();
  return db.getAllFromIndex('subclients', 'customer_id', customerId);
}